import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/hooks/useAuth";

export interface OperationsMomWithBrand {
  id: string;
  brand_id: string;
  user_id: string;
  filename: string;
  transcript_content: string;
  summary?: string;
  status: string;
  created_at: string;
  updated_at: string;
  brand?: {
    id: string;
    name: string;
    slug: string;
  };
}

export interface SalesMomWithBrand {
  id: string;
  brand_id: string;
  user_id: string;
  client_name: string;
  client_email: string | null;
  client_website: string | null;
  description: string | null;
  transcript_content: string | null;
  status: string;
  created_at: string;
  updated_at: string;
  brand?: {
    id: string;
    name: string;
    slug: string;
  };
}

interface UseMomsAllBrandsOptions {
  enabled?: boolean;
}

export const useMomsAllBrands = (options: UseMomsAllBrandsOptions = {}) => {
  const { enabled = true } = options;
  const { toast } = useToast();
  const { user } = useAuth();

  const getUserAccessibleBrandIds = async (): Promise<string[]> => {
    if (!user) return [];

    try {
      // Get brands owned by user
      const { data: ownedBrands } = await supabase
        .from('brands')
        .select('id')
        .eq('user_id', user.id);

      // Get brands accessible through user_brand_access
      const { data: accessibleBrands } = await supabase
        .from('user_brand_access')
        .select('brand_id')
        .eq('user_id', user.id);

      const allBrandIds = [
        ...(ownedBrands || []).map(b => b.id),
        ...(accessibleBrands || []).map(b => b.brand_id)
      ];

      return [...new Set(allBrandIds)]; // Remove duplicates
    } catch (error) {
      console.error('Error getting accessible brand IDs:', error);
      return [];
    }
  };

  const fetchOperationsMoms = async (): Promise<OperationsMomWithBrand[]> => {
    if (!user) return [];

    try {
      const accessibleBrandIds = await getUserAccessibleBrandIds();
      if (accessibleBrandIds.length === 0) return [];

      const { data, error } = await supabase
        .from('operations_moms')
        .select(`
          id,
          brand_id,
          user_id,
          filename,
          transcript_content,
          summary,
          status,
          created_at,
          updated_at,
          brands!inner(id, name, slug)
        `)
        .in('brand_id', accessibleBrandIds)
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Error fetching operations MOMs:', error);
        throw error;
      }

      return (data || []).map((mom: any) => ({
        ...mom,
        brand: Array.isArray(mom.brands) ? mom.brands[0] : mom.brands,
        brands: undefined // Remove the brands property to match interface
      }));
    } catch (error) {
      console.error('Error in fetchOperationsMoms:', error);
      return [];
    }
  };

  const fetchSalesMoms = async (): Promise<SalesMomWithBrand[]> => {
    if (!user) return [];

    try {
      const accessibleBrandIds = await getUserAccessibleBrandIds();
      if (accessibleBrandIds.length === 0) return [];

      const { data, error } = await supabase
        .from('os_sales_moms')
        .select(`
          id,
          brand_id,
          user_id,
          client_name,
          client_email,
          client_website,
          description,
          transcript_content,
          status,
          created_at,
          updated_at,
          brands!inner(id, name, slug)
        `)
        .in('brand_id', accessibleBrandIds)
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Error fetching sales MOMs:', error);
        throw error;
      }

      return (data || []).map((mom: any) => ({
        ...mom,
        brand: Array.isArray(mom.brands) ? mom.brands[0] : mom.brands,
        brands: undefined // Remove the brands property to match interface
      }));
    } catch (error) {
      console.error('Error in fetchSalesMoms:', error);
      return [];
    }
  };

  const {
    data: operationsMoms = [],
    isLoading: isLoadingOperations
  } = useQuery({
    queryKey: ['operations-moms-all-brands', user?.id],
    queryFn: fetchOperationsMoms,
    enabled: !!user && enabled,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    refetchOnWindowFocus: false,
  });

  const {
    data: salesMoms = [],
    isLoading: isLoadingSales
  } = useQuery({
    queryKey: ['sales-moms-all-brands', user?.id],
    queryFn: fetchSalesMoms,
    enabled: !!user && enabled,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    refetchOnWindowFocus: false,
  });

  return {
    operationsMoms,
    salesMoms,
    isLoading: enabled ? (isLoadingOperations || isLoadingSales) : false,
  };
};
